import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useCompany, PageHeader, EmptyState, Money } from "@/components/Shared";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { fmtDate } from "@/lib/format";
import { toast } from "sonner";
import { BellRing, Loader2, Mail, MessageCircle, Phone } from "lucide-react";

const CH = { email: [Mail, "Email"], whatsapp: [MessageCircle, "WhatsApp"], sms: [Phone, "SMS"] };
const ST = { sent: "bg-emerald-100 text-emerald-700", simulated: "bg-amber-100 text-amber-700", failed: "bg-rose-100 text-rose-700" };

export default function Relances() {
  const { companyId } = useCompany();
  const [notifs, setNotifs] = useState(null);
  const [running, setRunning] = useState(false);

  const load = () => api.get("/notifications", { params: { company_id: companyId } }).then(({ data }) => setNotifs(data.notifications));
  useEffect(() => { if (companyId) load(); }, [companyId]);

  const run = async () => {
    setRunning(true);
    try { const { data } = await api.post("/dunning/run", {}, { params: { company_id: companyId } });
      toast.success(`${data.processed} facture(s) relancée(s) — email réel + WhatsApp/SMS simulés`); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } finally { setRunning(false); }
  };

  return (
    <div data-testid="relances-page">
      <PageHeader title="Relances automatiques" subtitle="Rappels d'impayés J-3, J, J+3, J+7 (email réel, WhatsApp/SMS simulés). Exécution quotidienne automatique." action={
        <Button onClick={run} disabled={running} className="rounded-full active:scale-95 transition-transform" data-testid="run-dunning-button">
          {running ? <Loader2 className="w-4 h-4 animate-spin" /> : <><BellRing className="w-4 h-4 mr-1" /> Lancer les relances</>}
        </Button>} />
      <Card className="rounded-md border-border shadow-sm overflow-hidden">
        <div className="px-5 py-3 font-display font-semibold border-b border-border">Historique des notifications</div>
        {notifs === null ? <div className="py-16 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
          : notifs.length === 0 ? <EmptyState title="Aucune relance envoyée" hint="Cliquez sur « Lancer les relances »." />
          : <Table>
              <TableHeader><TableRow><TableHead>Date</TableHead><TableHead>Canal</TableHead><TableHead>Destinataire</TableHead><TableHead>Message</TableHead><TableHead>Statut</TableHead></TableRow></TableHeader>
              <TableBody>{notifs.map((n) => { const [Icon, label] = CH[n.channel] || [Mail, n.channel]; return (
                <TableRow key={n.id} data-testid={`notif-row-${n.id}`}>
                  <TableCell className="text-sm text-muted-foreground">{fmtDate(n.sent_at)}</TableCell>
                  <TableCell><span className="inline-flex items-center gap-1.5 text-sm"><Icon className="w-3.5 h-3.5 text-primary" />{label}</span></TableCell>
                  <TableCell className="text-sm">{n.to || "—"}</TableCell>
                  <TableCell className="text-sm max-w-xs truncate">{n.message}</TableCell>
                  <TableCell><Badge className={`border-0 ${ST[n.status] || "bg-slate-100"}`}>{n.status === "sent" ? "Envoyé" : n.status === "simulated" ? "Simulé" : "Échec"}</Badge></TableCell>
                </TableRow>); })}
              </TableBody>
            </Table>}
      </Card>
    </div>
  );
}
